import { useState } from 'react'
import { MessageSquare, Users, Edit, Zap, CheckCircle2, Clock, Share2, ArrowRight } from 'lucide-react'

export default function DocCollaborationPage() {
  const [stats] = useState({
    activeCollab: 245,
    comments: 3847,
    editingNow: 34,
    teamMembers: 156,
  })

  const features = [
    { title: '💬 Comments & Annotations', description: 'Leave comments and mention team members', icon: MessageSquare, color: 'from-blue-600 to-blue-500', stats: '3,847 Comments' },
    { title: '👥 Real-time Collaboration', description: 'Multiple users editing documents simultaneously', icon: Users, color: 'from-purple-600 to-purple-500', stats: '34 Editing Now' },
    { title: '✏️ Track Changes', description: 'See who changed what and when', icon: Edit, color: 'from-green-600 to-green-500', stats: 'All Changes Tracked' },
    { title: '⚡ Instant Updates', description: 'See changes in real-time as they happen', icon: Zap, color: 'from-yellow-600 to-yellow-500', stats: 'Real-time Sync' },
    { title: '✓ Approval Workflows', description: 'Route documents for review and approval', icon: CheckCircle2, color: 'from-teal-600 to-teal-500', stats: '98% Approvals' },
    { title: '⏰ Version History', description: 'Access any previous version of documents', icon: Clock, color: 'from-cyan-600 to-cyan-500', stats: 'Full History' },
    { title: '📤 Share & Notify', description: 'Share documents with instant notifications', icon: Share2, color: 'from-indigo-600 to-indigo-500', stats: '245 Active' },
    { title: '🔐 Permission Control', description: 'Granular permissions at document level', icon: Zap, color: 'from-red-600 to-red-500', stats: 'Full Control' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 p-8 text-white shadow-2xl border border-purple-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-pink-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <MessageSquare className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Document Collaboration</h1>
                </div>
                <p className="text-purple-100 text-lg font-semibold">Real-time Team Document Collaboration</p>
                <p className="text-purple-200 text-sm mt-1">Comments • Real-time Editing • Approvals • Version History</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.activeCollab}</div>
                <div className="text-sm text-purple-100">Active Collaborations</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Active Collaborations', value: stats.activeCollab, icon: Users, color: 'from-purple-600 to-purple-500' },
            { label: 'Total Comments', value: stats.comments, icon: MessageSquare, color: 'from-pink-600 to-pink-500' },
            { label: 'Editing Right Now', value: stats.editingNow, icon: Edit, color: 'from-blue-600 to-blue-500' },
            { label: 'Team Members', value: stats.teamMembers, icon: Users, color: 'from-green-600 to-green-500' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Collaboration Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon
              return (
                <div key={idx} className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1">
                  <div className={`h-1 w-full bg-gradient-to-r ${feature.color} rounded-full mb-4 shadow-lg`}></div>
                  <div className={`bg-gradient-to-br ${feature.color} p-3 rounded-lg w-fit mb-4 shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2 text-lg">{feature.title}</h3>
                  <p className="text-blue-300 text-sm mb-4">{feature.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">{feature.stats}</span>
                    <ArrowRight className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
